import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtRp, fmtNum, fmtDate } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export function CostCorrectionDialog({ item, onClose, onSaved }) {
  const [cost, setCost] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setCost(item?.unit_cost ? String(Math.round(item.unit_cost)) : ""); setReason(""); }, [item]);
  if (!item) return null;
  const unit = item.unit || "MC";
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.patch(`/inventory/${item.id}/cost`, { unit_cost: Number(cost), reason });
      toast.success(`Harga beli ${item.batch_code} diperbarui ke ${fmtRp(r.data.unit_cost)}/${unit}`);
      onSaved(r.data); onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="cost-correction-dialog">
        <DialogHeader>
          <DialogTitle>Koreksi Harga Beli</DialogTitle>
          <DialogDescription><span className="font-mono text-sky-800 font-semibold">{item.batch_code}</span> — {item.species_name} {item.size} • stok {fmtNum(item.quantity_mc)} {unit}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Harga saat ini</Label><div className="h-10 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 font-mono text-sm text-slate-600" data-testid="cost-current">{item.unit_cost ? `${fmtRp(item.unit_cost)}/${unit}` : "—"}</div></div>
            <div className="space-y-1.5"><Label>Harga baru per {unit}</Label><MoneyInput value={cost} onChange={setCost} required autoFocus data-testid="input-cost-new" /></div>
          </div>
          <div className="space-y-1.5"><Label>Alasan koreksi <span className="text-rose-500">*</span></Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="cth. salah ketik saat stok masuk" required data-testid="input-cost-reason" /></div>
          <p className="text-[11px] text-slate-600 bg-sky-50 border border-sky-100 rounded-lg px-3 py-2">Hanya harga di batch ini yang berubah. Jurnal pembelian lama tidak disentuh; HPP untuk penjualan berikutnya memakai harga baru.</p>
          {item.cost_history?.length > 0 && (
            <div className="text-xs" data-testid="cost-history">
              <div className="font-semibold text-slate-700 mb-1">Riwayat koreksi</div>
              <ul className="space-y-1 max-h-32 overflow-auto">
                {[...item.cost_history].reverse().map((h, i) => (
                  <li key={i} className="flex justify-between gap-3 border-b border-dashed border-slate-200 pb-1">
                    <span className="text-slate-600 truncate">{h.reason} <span className="text-slate-400">• {h.by}</span></span>
                    <span className="font-mono whitespace-nowrap">{h.old_cost ? fmtRp(h.old_cost) : "—"} → {fmtRp(h.new_cost)} <span className="text-slate-400 font-sans">{fmtDate(h.at.slice(0, 10))}</span></span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="btn-cost-cancel">Batal</Button>
            <Button type="submit" disabled={saving || cost === "" || !reason.trim()} className="bg-sky-600 hover:bg-sky-700" data-testid="btn-cost-submit">{saving ? "Menyimpan..." : "Simpan Harga"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
